import React, { useState, useEffect } from 'react';
import { Save, Upload, X, Plus, Trash2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function CompanySettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  const [settings, setSettings] = useState({
    company_name: '',
    email: '',
    phone: '',
    website: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    pincode: '',
    country: 'India',
    msme_number: '',
    trade_license_number: '',
    logo_url: '',
    bank_details: {
      bank_name: '',
      account_number: '',
      account_holder_name: '',
      ifsc_code: '',
      branch: '',
      account_type: ''
    },
    tax_config: {
      gst_enabled: false,
      gst_number: '',
      cgst_rate: 9.0,
      sgst_rate: 9.0,
      igst_rate: 18.0
    },
    proposal_prefix: 'PROP',
    invoice_prefix: 'INV',
    cash_invoice_prefix: 'CASH',
    proposal_validity_days: 30,
    proposal_terms: '',
    invoice_terms: '',
    custom_fields: []
  });
  
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [newCustomField, setNewCustomField] = useState({ key: '', value: '' });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/company-settings`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
        if (data.logo_url) {
          setLogoPreview(`${BACKEND_URL}${data.logo_url}`);
        }
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      setMessage({ type: 'error', text: 'Failed to load company settings' });
    } finally {
      setLoading(false);
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });
    
    try {
      // Upload logo if changed
      if (logoFile) {
        const formData = new FormData();
        formData.append('file', logoFile);
        
        const uploadResponse = await fetch(`${BACKEND_URL}/api/company-settings/upload-logo`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
          },
          body: formData
        });
        
        if (uploadResponse.ok) {
          const uploadData = await uploadResponse.json();
          settings.logo_url = uploadData.logo_url;
        }
      }
      
      // Save settings
      const response = await fetch(`${BACKEND_URL}/api/company-settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        },
        body: JSON.stringify(settings)
      });
      
      if (response.ok) {
        const savedData = await response.json();
        setSettings(savedData);
        setMessage({ type: 'success', text: 'Company settings saved successfully!' });
        setLogoFile(null);
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.detail || 'Failed to save settings' });
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      setMessage({ type: 'error', text: 'Failed to save settings' });
    } finally {
      setSaving(false);
    }
  };

  const addCustomField = () => {
    if (newCustomField.key && newCustomField.value) {
      setSettings({
        ...settings,
        custom_fields: [...settings.custom_fields, { ...newCustomField }]
      });
      setNewCustomField({ key: '', value: '' });
    }
  };

  const removeCustomField = (index) => {
    const updated = settings.custom_fields.filter((_, i) => i !== index);
    setSettings({ ...settings, custom_fields: updated });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-bold text-gray-800">Company Settings</h1>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              <Save size={20} />
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>

          {message.text && (
            <div className={`mb-4 p-3 rounded-lg ${
              message.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
            }`}>
              {message.text}
            </div>
          )}

          {/* Basic Information */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-700 mb-4">Basic Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Company Name *</label>
                <input
                  type="text"
                  value={settings.company_name}
                  onChange={(e) => setSettings({ ...settings, company_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Visaasure Pvt. Ltd."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <input
                  type="email"
                  value={settings.email}
                  onChange={(e) => setSettings({ ...settings, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="info@visaasure.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                <input
                  type="text"
                  value={settings.phone}
                  onChange={(e) => setSettings({ ...settings, phone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="+91 1234567890"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
                <input
                  type="text"
                  value={settings.website}
                  onChange={(e) => setSettings({ ...settings, website: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="www.visaasure.com"
                />
              </div>
            </div>
          </div>

          {/* Logo */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-700 mb-4">Company Logo</h2>
            <div className="flex items-center gap-4">
              {logoPreview && (
                <div className="w-32 h-32 border border-gray-300 rounded-lg overflow-hidden flex items-center justify-center bg-gray-50">
                  <img src={logoPreview} alt="Logo" className="max-w-full max-h-full object-contain" />
                </div>
              )}
              <div>
                <label className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg cursor-pointer hover:bg-gray-200">
                  <Upload size={20} />
                  <span>Upload Logo</span>
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/svg+xml"
                    onChange={handleLogoChange}
                    className="hidden"
                  />
                </label>
                <p className="text-xs text-gray-500 mt-1">PNG, JPG, SVG (Max 2MB)</p>
              </div>
            </div>
          </div>

          {/* Address */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-700 mb-4">Address</h2>
            <div className="grid grid-cols-1 gap-4">
              <input
                type="text"
                value={settings.address_line1}
                onChange={(e) => setSettings({ ...settings, address_line1: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Address Line 1"
              />
              <input
                type="text"
                value={settings.address_line2}
                onChange={(e) => setSettings({ ...settings, address_line2: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Address Line 2"
              />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <input
                  type="text"
                  value={settings.city}
                  onChange={(e) => setSettings({ ...settings, city: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="City"
                />
                <input
                  type="text"
                  value={settings.state}
                  onChange={(e) => setSettings({ ...settings, state: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="State"
                />
                <input
                  type="text"
                  value={settings.pincode}
                  onChange={(e) => setSettings({ ...settings, pincode: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Pincode"
                />
              </div>
            </div>
          </div>

          {/* Legal Information */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-700 mb-4">Legal Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">MSME Number</label>
                <input
                  type="text"
                  value={settings.msme_number}
                  onChange={(e) => setSettings({ ...settings, msme_number: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Trade License Number</label>
                <input
                  type="text"
                  value={settings.trade_license_number}
                  onChange={(e) => setSettings({ ...settings, trade_license_number: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Tax Configuration */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-700 mb-4">Tax Configuration</h2>
            <div className="mb-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={settings.tax_config.gst_enabled}
                  onChange={(e) => setSettings({
                    ...settings,
                    tax_config: { ...settings.tax_config, gst_enabled: e.target.checked }
                  })}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">Enable GST</span>
              </label>
            </div>
            {settings.tax_config.gst_enabled && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">GST Number *</label>
                  <input
                    type="text"
                    value={settings.tax_config.gst_number}
                    onChange={(e) => setSettings({
                      ...settings,
                      tax_config: { ...settings.tax_config, gst_number: e.target.value }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="22AAAAA0000A1Z5"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">CGST Rate (%)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={settings.tax_config.cgst_rate || ''}
                    onChange={(e) => setSettings({
                      ...settings,
                      tax_config: { ...settings.tax_config, cgst_rate: e.target.value ? parseFloat(e.target.value) : 0 }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">SGST Rate (%)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={settings.tax_config.sgst_rate || ''}
                    onChange={(e) => setSettings({
                      ...settings,
                      tax_config: { ...settings.tax_config, sgst_rate: e.target.value ? parseFloat(e.target.value) : 0 }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">IGST Rate (%)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={settings.tax_config.igst_rate || ''}
                    onChange={(e) => setSettings({
                      ...settings,
                      tax_config: { ...settings.tax_config, igst_rate: e.target.value ? parseFloat(e.target.value) : 0 }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Bank Details */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-700 mb-4">Bank Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input
                type="text"
                value={settings.bank_details.bank_name}
                onChange={(e) => setSettings({
                  ...settings,
                  bank_details: { ...settings.bank_details, bank_name: e.target.value }
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Bank Name"
              />
              <input
                type="text"
                value={settings.bank_details.account_holder_name}
                onChange={(e) => setSettings({
                  ...settings,
                  bank_details: { ...settings.bank_details, account_holder_name: e.target.value }
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Account Holder Name"
              />
              <input
                type="text"
                value={settings.bank_details.account_number}
                onChange={(e) => setSettings({
                  ...settings,
                  bank_details: { ...settings.bank_details, account_number: e.target.value }
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Account Number"
              />
              <input
                type="text"
                value={settings.bank_details.ifsc_code}
                onChange={(e) => setSettings({
                  ...settings,
                  bank_details: { ...settings.bank_details, ifsc_code: e.target.value }
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="IFSC Code"
              />
              <input
                type="text"
                value={settings.bank_details.branch}
                onChange={(e) => setSettings({
                  ...settings,
                  bank_details: { ...settings.bank_details, branch: e.target.value }
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Branch"
              />
              <input
                type="text"
                value={settings.bank_details.account_type}
                onChange={(e) => setSettings({
                  ...settings,
                  bank_details: { ...settings.bank_details, account_type: e.target.value }
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Account Type (Savings/Current)"
              />
            </div>
          </div>

          {/* Proposal & Invoice Configuration */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-700 mb-4">Proposal & Invoice Configuration</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Proposal Prefix</label>
                <input
                  type="text"
                  value={settings.proposal_prefix}
                  onChange={(e) => setSettings({ ...settings, proposal_prefix: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="PROP"
                />
                <p className="text-xs text-gray-500 mt-1">Format: PROP/2025/001</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Prefix</label>
                <input
                  type="text"
                  value={settings.invoice_prefix}
                  onChange={(e) => setSettings({ ...settings, invoice_prefix: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="INV"
                />
                <p className="text-xs text-gray-500 mt-1">Format: INV/2025/001</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cash Invoice Prefix</label>
                <input
                  type="text"
                  value={settings.cash_invoice_prefix}
                  onChange={(e) => setSettings({ ...settings, cash_invoice_prefix: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="CASH"
                />
                <p className="text-xs text-gray-500 mt-1">Format: CASH/2025/001</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Proposal Validity (Days)</label>
                <input
                  type="number"
                  value={settings.proposal_validity_days || ''}
                  onChange={(e) => setSettings({ ...settings, proposal_validity_days: e.target.value ? parseInt(e.target.value) : 30 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Proposal Terms & Conditions</label>
                <textarea
                  value={settings.proposal_terms}
                  onChange={(e) => setSettings({ ...settings, proposal_terms: e.target.value })}
                  rows="4"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Default terms and conditions for proposals..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Terms & Conditions</label>
                <textarea
                  value={settings.invoice_terms}
                  onChange={(e) => setSettings({ ...settings, invoice_terms: e.target.value })}
                  rows="4"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Default terms and conditions for invoices..."
                />
              </div>
            </div>
          </div>

          {/* Custom Fields */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-700 mb-4">Custom Fields</h2>
            <div className="space-y-2 mb-4">
              {settings.custom_fields.map((field, index) => (
                <div key={index} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                  <div className="flex-1 grid grid-cols-2 gap-2">
                    <span className="font-medium text-sm">{field.key}:</span>
                    <span className="text-sm">{field.value}</span>
                  </div>
                  <button
                    onClick={() => removeCustomField(index)}
                    className="p-1 text-red-600 hover:bg-red-100 rounded"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={newCustomField.key}
                onChange={(e) => setNewCustomField({ ...newCustomField, key: e.target.value })}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Field Name"
              />
              <input
                type="text"
                value={newCustomField.value}
                onChange={(e) => setNewCustomField({ ...newCustomField, value: e.target.value })}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Field Value"
              />
              <button
                onClick={addCustomField}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
              >
                <Plus size={20} />
                Add
              </button>
            </div>
          </div>
        </div>
      </div>
  );
}
